// ABOUTME: Cloudflare Workers auth provider using httpOnly cookies + in-memory JWT
// ABOUTME: Session via cookies (set by server), JWT in memory for API requests

import { refreshToken as apiRefreshToken } from '../../lib/api'
import { clearTokens, getJwt, getSessionToken, setJwt, setSessionToken } from '../../lib/auth-token'
import type {
  AuthResult,
  AuthStateChangeCallback,
  IAuthProvider,
  Session,
  User,
} from '../interfaces'

interface BetterAuthUser {
  id: string
  email: string
  name?: string
  emailVerified: boolean
  image?: string | null
  createdAt: string
  updatedAt: string
}

interface BetterAuthSession {
  id: string
  token: string
  expiresAt: string
  userId?: string
}

function mapUser(raw: BetterAuthUser): User {
  return {
    id: raw.id,
    email: raw.email,
    emailConfirmed: raw.emailVerified ?? false,
    metadata: { name: raw.name, image: raw.image },
    createdAt: new Date(raw.createdAt),
    updatedAt: new Date(raw.updatedAt),
  }
}

function mapSession(rawSession: BetterAuthSession, user: User): Session {
  return {
    accessToken: rawSession.token,
    expiresAt: new Date(rawSession.expiresAt),
    user,
  }
}

export class CloudflareAuthProvider implements IAuthProvider {
  private readonly apiUrl: string
  private listeners: AuthStateChangeCallback[] = []

  constructor(apiUrl: string) {
    this.apiUrl = apiUrl
  }

  private async fetchJwt(): Promise<void> {
    const headers: Record<string, string> = {}
    const bearer = getSessionToken()
    if (bearer) {
      headers.Authorization = `Bearer ${bearer}`
    }
    const res = await fetch(`${this.apiUrl}/api/auth/token`, {
      credentials: 'include',
      headers,
    })
    if (res.ok) {
      const data = (await res.json()) as { token?: string }
      if (data.token) {
        setJwt(data.token)
      }
    }
  }

  async signUp(
    email: string,
    password: string,
    // biome-ignore lint/suspicious/noExplicitAny: dynamic user metadata
    metadata?: Record<string, any>
  ): Promise<AuthResult> {
    try {
      const response = await fetch(`${this.apiUrl}/api/auth/sign-up/email`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name: metadata?.name || '' }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        const rawMessage = (errorData as { message?: string }).message || ''
        const message = /already exists/i.test(rawMessage)
          ? 'Unable to create account. Please try again or sign in.'
          : rawMessage || 'Sign up failed'
        return { user: null, session: null, error: new Error(message) }
      }

      const data = (await response.json()) as {
        user: BetterAuthUser
        token: string
        session?: BetterAuthSession
      }
      const user = mapUser(data.user)
      const rawSession = data.session ?? {
        id: '',
        token: data.token,
        expiresAt: new Date(Date.now() + 7 * 86400000).toISOString(),
      }
      const session = mapSession(rawSession, user)

      if (data.token) setSessionToken(data.token)
      await this.fetchJwt()
      this.notifyListeners(session, user)

      return { user, session }
    } catch (err) {
      return {
        user: null,
        session: null,
        error: err instanceof Error ? err : new Error('Sign up failed'),
      }
    }
  }

  async signIn(email: string, password: string): Promise<AuthResult> {
    try {
      const response = await fetch(`${this.apiUrl}/api/auth/sign-in/email`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        const msg = (errorData as { message?: string }).message || 'Sign in failed'
        return { user: null, session: null, error: new Error(msg) }
      }

      const data = (await response.json()) as {
        user: BetterAuthUser
        token: string
        session?: BetterAuthSession
      }
      const user = mapUser(data.user)
      const rawSession = data.session ?? {
        id: '',
        token: data.token,
        expiresAt: new Date(Date.now() + 7 * 86400000).toISOString(),
      }
      const session = mapSession(rawSession, user)

      if (data.token) setSessionToken(data.token)
      await this.fetchJwt()
      this.notifyListeners(session, user)

      return { user, session }
    } catch (err) {
      return {
        user: null,
        session: null,
        error: err instanceof Error ? err : new Error('Sign in failed'),
      }
    }
  }

  async signOut(): Promise<void> {
    await fetch(`${this.apiUrl}/api/auth/sign-out`, {
      method: 'POST',
      credentials: 'include',
    })

    clearTokens()
    this.notifyListeners(null, null)
  }

  async getSession(): Promise<Session | null> {
    try {
      const headers: Record<string, string> = {}
      const bearer = getSessionToken()
      if (bearer) {
        headers.Authorization = `Bearer ${bearer}`
      }
      const response = await fetch(`${this.apiUrl}/api/auth/get-session`, {
        credentials: 'include',
        headers,
      })

      if (!response.ok) {
        return null
      }

      const data = (await response.json()) as { session: BetterAuthSession; user: BetterAuthUser }
      if (!data.session || !data.user) {
        return null
      }

      const user = mapUser(data.user)

      if (!getJwt()) {
        await this.fetchJwt()
      }

      return mapSession(data.session, user)
    } catch {
      return null
    }
  }

  async getUser(): Promise<User | null> {
    const session = await this.getSession()
    return session?.user ?? null
  }

  onAuthStateChange(callback: AuthStateChangeCallback): () => void {
    this.listeners.push(callback)

    return () => {
      this.listeners = this.listeners.filter((l) => l !== callback)
    }
  }

  async resetPassword(email: string): Promise<{ error?: Error }> {
    try {
      const response = await fetch(`${this.apiUrl}/api/auth/request-password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, redirectTo: `${window.location.origin}/auth` }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        return {
          error: new Error((errorData as { message?: string }).message || 'Password reset failed'),
        }
      }

      return {}
    } catch (err) {
      return { error: err instanceof Error ? err : new Error('Password reset failed') }
    }
  }

  async updateUser(
    metadata: Record<string, unknown>
  ): Promise<{ user: User | null; error?: Error }> {
    try {
      const response = await fetch(`${this.apiUrl}/api/auth/update-user`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(metadata),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        return {
          user: null,
          error: new Error((errorData as { message?: string }).message || 'Update failed'),
        }
      }

      const data = (await response.json()) as { user: BetterAuthUser }
      return { user: mapUser(data.user) }
    } catch (err) {
      return { user: null, error: err instanceof Error ? err : new Error('Update failed') }
    }
  }

  async refreshToken(): Promise<Session | null> {
    const jwt = await apiRefreshToken()
    if (!jwt) return null
    return this.getSession()
  }

  private notifyListeners(session: Session | null, user: User | null): void {
    for (const listener of this.listeners) {
      listener(session, user)
    }
  }
}
